import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  VStack,
  Text,
  Badge,
  Divider,
  Flex,
  Avatar,
  useToast,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputRightElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { markMessagesAsRead, deleteMessage } from '../../Redux/Login/action';
import { WarningIcon, DeleteIcon, CheckIcon, SearchIcon } from '@chakra-ui/icons';

// Message bubble component
const MessageBubble = ({ message, handleMarkAsRead, handleDelete }) => {
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = React.useState(false);
  const cancelRef = React.useRef();
  
  return (
    <Box width="100%" mb={4}>
      <Flex justify="space-between" mb={1}>
        <Flex align="center">
          <Avatar size="xs" name={message.fromBurrowerId} bg="purple.500" mr={2} />
          <Text fontSize="sm" fontWeight="bold">{message.fromBurrowerId} <Badge colorScheme="purple">Admin</Badge></Text>
        </Flex>
        <Text fontSize="xs" color="gray.500">
          {message.formattedTime}
        </Text>
      </Flex>
      
      <Box 
        bg="purple.900" 
        p={3} 
        borderRadius="lg" 
        borderTopLeftRadius="sm"
        maxW="90%"
        position="relative"
        boxShadow="sm"
      >
        <Text color="white">{message.adminMessageContents}</Text>
        
        <Flex mt={2} justify="flex-end" opacity={0.7}>
          {!message.isRead && (
            <Tooltip label="Mark as read">
              <IconButton
                icon={<CheckIcon />}
                size="xs"
                variant="ghost"
                onClick={() => handleMarkAsRead(message.adminMessageId)}
                aria-label="Mark as read"
              />
            </Tooltip>
          )}
          
          <Tooltip label="Delete">
            <IconButton
              icon={<DeleteIcon />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => setIsDeleteAlertOpen(true)}
              aria-label="Delete message"
              ml={1}
            />
          </Tooltip>
        </Flex>
      </Box>
      
      {/* Delete confirmation alert */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteAlertOpen(false)}
        size="sm"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Message
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this important message? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteAlertOpen(false)}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => {
                  handleDelete(message.adminMessageId);
                  setIsDeleteAlertOpen(false);
                }}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

const AdminMessagesModal = ({ isOpen, onClose, messages = [] }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { markingRead, deletingMessage } = useSelector(state => state.auth.messages || {});
  const [deletingId, setDeletingId] = React.useState(null);
  const [localMessages, setLocalMessages] = React.useState(messages);
  const [searchQuery, setSearchQuery] = React.useState('');
  
  React.useEffect(() => {
    setLocalMessages(messages);
  }, [messages]);
  
  // Format time ago function (replacement for date-fns formatDistanceToNow)
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    // Time intervals in seconds
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1
    };
    
    let counter;
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      counter = Math.floor(seconds / secondsInUnit);
      if (counter > 0) {
        return counter === 1 ? `1 ${unit} ago` : `${counter} ${unit}s ago`;
      }
    }
    
    return 'just now';
  };
  
  // Process messages with formatted time
  const processedMessages = localMessages.map(msg => ({
    ...msg,
    formattedTime: formatTimeAgo(msg.sentAt)
  }));
  
  // Sort messages by date, newest first
  const sortedMessages = [...processedMessages].sort((a, b) => 
    new Date(b.sentAt) - new Date(a.sentAt)
  );
  
  // Filter messages based on search query
  const filteredMessages = searchQuery 
    ? sortedMessages.filter(msg => 
        msg.adminMessageContents.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.fromBurrowerId.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sortedMessages;
  
  // Find unread message IDs
  const unreadMessageIds = localMessages
    .filter(msg => !msg.isRead)
    .map(msg => msg.adminMessageId);
  
  const handleMarkAllAsRead = async () => {
    if (unreadMessageIds.length === 0) {
      toast({
        title: "No unread messages",
        status: "info",
        duration: 3000,
      });
      return;
    }
    
    const result = await dispatch(markMessagesAsRead(unreadMessageIds, 'admin'));
    
    if (result.success) {
      toast({
        title: "Messages marked as read",
        description: `${result.updateCount} messages updated`,
        status: "success",
        duration: 3000,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to mark messages as read",
        status: "error",
        duration: 3000,
      });
    }
  };
  
  const handleMarkAsRead = async (messageId) => {
    const result = await dispatch(markMessagesAsRead([messageId], 'admin'));
    
    if (result.success) {
      toast({
        title: "Message marked as read",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to mark message as read",
        status: "error",
        duration: 3000,
      });
    }
  };
  
  const handleDeleteMessage = async (messageId) => {
    setDeletingId(messageId);
    try {
      const result = await dispatch(deleteMessage(messageId, 'admin'));
      
      if (result.success) {
        toast({
          title: "Message deleted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Update local state to remove the message
        setLocalMessages(prev => prev.filter(msg => msg.adminMessageId !== messageId));
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete message",
          status: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
    } finally {
      setDeletingId(null);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh" bg="gray.800" color="white">
        <ModalHeader display="flex" justifyContent="space-between" alignItems="center">
          <Text>Important Messages</Text>
          <Badge colorScheme="purple" fontSize="0.8em" ml={2}>
            {unreadMessageIds.length} Unread
          </Badge>
        </ModalHeader>
        <ModalCloseButton />
        
        <Box px={6} pb={2}>
          <InputGroup size="md">
            <Input
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              pr="4.5rem"
            />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={() => setSearchQuery('')}>
                {searchQuery ? 'Clear' : <SearchIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </Box>
        
        <ModalBody>
          {markingRead && (
            <Flex justify="center" my={4}>
              <Spinner />
            </Flex>
          )}
          
          {localMessages.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text fontSize="lg">No important messages</Text>
              <Text color="gray.500">System and administrative messages will appear here</Text>
            </Box>
          ) : filteredMessages.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text fontSize="lg">No matching messages</Text>
              <Text color="gray.500">Try a different search term</Text>
              <Button mt={4} size="sm" onClick={() => setSearchQuery('')}>Clear Search</Button>
            </Box>
          ) : (
            <VStack spacing={0} align="stretch" divider={<Divider />}>
              {filteredMessages.map(message => (
                <MessageBubble
                  key={message.adminMessageId}
                  message={message}
                  handleMarkAsRead={handleMarkAsRead}
                  handleDelete={handleDeleteMessage}
                />
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button 
            colorScheme="purple" 
            mr={3} 
            onClick={handleMarkAllAsRead}
            isDisabled={unreadMessageIds.length === 0}
            size="sm"
          >
            Mark All as Read
          </Button>
          <Button variant="ghost" onClick={onClose} size="sm">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminMessagesModal;
